import { useEffect, useMemo, useState } from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import type { ParagraphDto } from '../../api/queries/books';
import { useWritingSessionStore } from '../../stores/writingSession.store';

interface WordCountProps {
  paragraphs: ParagraphDto[];
  chapterCount?: number;
}

function countWords(html: string): number {
  const text = html.replace(/<[^>]*>/g, '').trim();
  return text ? text.split(/\s+/).length : 0;
}

function formatElapsed(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
}

export function WordCount({ paragraphs, chapterCount }: WordCountProps) {
  const chapterWords = useMemo(
    () => paragraphs.reduce((sum, p) => sum + countWords(p.contentHtml), 0),
    [paragraphs],
  );

  const {
    sessionStartTime,
    startingWordCount,
    isActive,
    start,
    updateWordCount,
  } = useWritingSessionStore();
  const [now, setNow] = useState(Date.now());

  // Start session on first render if not active.
  useEffect(() => {
    if (!isActive && !sessionStartTime) {
      start(chapterWords);
    }
  }, [isActive, sessionStartTime, chapterWords, start]);

  // Update current word count on paragraph changes.
  useEffect(() => {
    updateWordCount(chapterWords);
  }, [chapterWords, updateWordCount]);

  // Tick the clock every 30s for elapsed time display.
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(interval);
  }, []);

  const formattedChapter = chapterWords.toLocaleString();
  const delta = chapterWords - startingWordCount;
  const deltaLabel = delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : null;
  const elapsed = sessionStartTime ? now - sessionStartTime : 0;

  const bookEstimate =
    chapterCount && chapterCount > 0
      ? `~${(chapterWords * chapterCount).toLocaleString()} words`
      : null;

  const tooltipText = [
    `Chapter: ${formattedChapter} words`,
    bookEstimate ? `Book: ${bookEstimate}` : null,
    deltaLabel ? `Session: ${deltaLabel} words` : null,
    elapsed > 0 ? `Time: ${formatElapsed(elapsed)}` : null,
  ]
    .filter(Boolean)
    .join(' \u00B7 ');

  return (
    <Tooltip title={tooltipText}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Chip
          size="small"
          label={`${formattedChapter} words`}
          variant="outlined"
        />
        {deltaLabel && (
          <Typography
            variant="caption"
            sx={{
              color: delta > 0 ? 'success.main' : 'error.main',
              fontWeight: 600,
              fontSize: 11,
            }}
          >
            {deltaLabel}
          </Typography>
        )}
        {elapsed > 60_000 && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontSize: 11 }}
          >
            {formatElapsed(elapsed)}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}
